import Panel from '../Panel';
import { useTheme } from '@mui/system';
import PropTypes from 'prop-types';

/**
 * Helper function to convert values between 0 and 1 into a rounded percentage string.
 * @param {Number} value - Value to convert to percentage
 * @returns {string} - Percentage
 */
function convertToPercentage(value) {
    if (value) {
        return Math.round(value * 100) + '%';
    }
    return '';
}

/* Sizes correspond directly with Figma designs exported as SVG*/
const ellipse_sizing = {
    baScore: {
        x: 85,
        y: 85,
        cx: 85,
        cy: 85,
        r: 85
    },
    payoff: {
        x: 230,
        y: 145,
        cx: 230,
        cy: 145,
        r: 60
    },
    hitrate: {
        x: 329,
        y: 67,
        cx: 329,
        cy: 67,
        r: 60
    }
};

/**
 * Renders a panel with three circle elements, each showing a different EBAB metric
 * (BA score, payoff, hit rate) for the currently selected portfolio.
 *
 * @component
 * @param {Object} props
 * @param {Object} selectedPortfolio - Currently selected portfolio
 * @returns {React.ReactElement} The rendered ProScorePanel component
 */
const ProScorePanel = ({ selectedPortfolio }) => {
    const theme = useTheme();

    const commonTextStyle = {
        fill: theme.palette.kimberley.darkest,
        textAnchor: 'middle'
    };

    return (
        <Panel
            sx={{
                position: 'relative',
                height: '100%',
                padding: 3,
                paddingLeft: 3.5,
                paddingRight: 3.5
            }}
            data-analyticsid={'portfolio-drilldown-ban'}
        >
            <svg width='100%' height='100%' viewBox='0 0 389 205'>
                <circle
                    data-analyticsid='ba-score'
                    {...ellipse_sizing.baScore}
                    fill={theme.palette.background.score_ellipse}
                />
                <text
                    {...ellipse_sizing.baScore}
                    dy={-25}
                    style={{ ...commonTextStyle, fontSize: 18 }}
                >
                    Behavioral
                </text>
                <text
                    {...ellipse_sizing.baScore}
                    dy={-3}
                    style={{ ...commonTextStyle, fontSize: 18 }}
                >
                    Alpha® Score
                </text>
                <text
                    {...ellipse_sizing.baScore}
                    dy={40}
                    style={{
                        ...commonTextStyle,
                        fontSize: 40,
                        fontWeight: 500
                    }}
                >
                    {selectedPortfolio?.baScore.toFixed(1)}
                </text>

                <circle
                    data-analyticsid='payoff'
                    {...ellipse_sizing.payoff}
                    fill={theme.palette.background.score_ellipse}
                />
                <text
                    {...ellipse_sizing.payoff}
                    dy={-22}
                    style={{ ...commonTextStyle, fontSize: 15 }}
                >
                    Decision
                </text>
                <text
                    {...ellipse_sizing.payoff}
                    dy={-4}
                    style={{ ...commonTextStyle, fontSize: 15 }}
                >
                    Payoff
                </text>
                <text
                    {...ellipse_sizing.payoff}
                    dy={27}
                    style={{
                        ...commonTextStyle,
                        fontSize: 27,
                        fontWeight: 500
                    }}
                >
                    {convertToPercentage(selectedPortfolio?.payoff)}
                </text>

                <circle
                    data-analyticsid='hit-rate'
                    {...ellipse_sizing.hitrate}
                    fill={theme.palette.background.score_ellipse}
                />
                <text
                    {...ellipse_sizing.hitrate}
                    dy={-22}
                    style={{ ...commonTextStyle, fontSize: 15 }}
                >
                    Decision
                </text>
                <text
                    {...ellipse_sizing.hitrate}
                    dy={-4}
                    style={{ ...commonTextStyle, fontSize: 15 }}
                >
                    Hit Rate
                </text>
                <text
                    {...ellipse_sizing.hitrate}
                    dy={27}
                    style={{
                        ...commonTextStyle,
                        fontSize: 27,
                        fontWeight: 500
                    }}
                >
                    {convertToPercentage(selectedPortfolio?.hitRate)}
                </text>
            </svg>
        </Panel>
    );
};

ProScorePanel.propTypes = {
    selectedPortfolio: PropTypes.object
};

export default ProScorePanel;
